import React from 'react';
import {
  Card,
  CardContent,
  Avatar,
  Typography,
  Box,
  Chip,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  MoreVert as MoreIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const ClientCard = ({ 
  client, 
  onClick, 
  onMenuClick,
  showScore = true 
}) => {
  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return (first + last).toUpperCase();
  };

  return (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3
        }
      }}
      onClick={() => onClick?.(client)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar 
            sx={{ 
              mr: 2, 
              bgcolor: 'primary.main',
              width: 48,
              height: 48
            }}
            src={client.avatar_url}
          >
            {getInitials(client.first_name, client.last_name)}
          </Avatar>
          
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  {client.salutation} {client.first_name} {client.last_name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip
                    icon={client.is_active ? <ActiveIcon /> : <InactiveIcon />}
                    label={client.is_active ? 'Active' : 'Inactive'}
                    color={client.is_active ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                  {client.labels?.map((label) => (
                    <Chip 
                      key={label} 
                      label={label} 
                      size="small" 
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
              
              <IconButton 
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onMenuClick?.(e, client);
                }}
              >
                <MoreIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 2 }}>
          {client.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <EmailIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 16 }} />
              <Typography variant="body2" color="text.secondary">
                {client.email}
              </Typography>
            </Box>
          )}
          {(client.mobile || client.phone) && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <PhoneIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 16 }} />
              <Typography variant="body2" color="text.secondary">
                {client.mobile || client.phone}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Metrics */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Last Visit
            </Typography>
            <Typography variant="body2">
              {client.last_visit_date 
                ? formatDistanceToNow(new Date(client.last_visit_date), { addSuffix: true })
                : 'Never'
              }
            </Typography>
          </Box>
          
          {client.total_revenue && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">
                Total Revenue
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                ${client.total_revenue.toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Client Score */}
        {showScore && client.pabau_score !== undefined && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Client Score
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {client.pabau_score}/100
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={client.pabau_score}
              color={getScoreColor(client.pabau_score)}
              sx={{ height: 6, borderRadius: 1 }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientCard;